import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Slider,
  Stack,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";

export type LogoEditorProps = {
  open: boolean;
  file: File | null;
  onClose: () => void;
  onSave: (processedBlob: Blob) => void;
};

export const LogoEditor = ({
  open,
  file,
  onClose,
  onSave,
}: LogoEditorProps) => {
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [rasterSrc, setRasterSrc] = useState<string | null>(null);
  const [imageEl, setImageEl] = useState<HTMLImageElement | null>(null);
  const [viewBox, setViewBox] = useState<string | null>(null);
  const [aspectRatio, setAspectRatio] = useState(1);
  // A file we can't read leaves the canvas blank. Say why on screen — this is
  // used on mobile, where a console message reaches nobody.
  const [loadError, setLoadError] = useState<string | null>(null);

  // Editor state
  // Zoom level (1 = fits in 256x256 box initially)
  const [zoom, setZoom] = useState(1);
  // Position relative to the top-left of the 256x256 crop area
  // Initial position should center the image
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const lastMousePos = useRef({ x: 0, y: 0 });
  const lastPinchDistance = useRef<number | null>(null);

  // Parse the selected file. The parent remounts this editor (keyed on the
  // file) whenever the selection changes, so fresh state already starts null
  // and this only needs to handle the present-file case.
  //
  // Every callback checks `active` first: a run whose cleanup has fired must
  // not touch state. StrictMode runs this effect, its cleanup, and the effect
  // again on mount, so a stale run's callbacks are a real possibility (the
  // SVG reader's in particular).
  useEffect(() => {
    if (!file) return;
    let active = true;

    const isSvg = file.type === "image/svg+xml";

    if (isSvg) {
      const reader = new FileReader();
      reader.onerror = () => {
        if (active)
          setLoadError(`Couldn't read ${file.name}. Try picking it again.`);
      };
      reader.onload = (e) => {
        if (!active) return;
        const text = e.target?.result as string;
        if (!text) {
          setLoadError(`${file.name} is empty.`);
          return;
        }

        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(text, "image/svg+xml");
          const svg = doc.documentElement;

          if (svg.tagName.toLowerCase() !== "svg") {
            setLoadError(
              `${file.name} isn't a valid SVG — its contents don't start with an <svg> element.`,
            );
            return;
          }

          // Extract or calculate viewBox
          let vb = svg.getAttribute("viewBox");
          let w = parseFloat(svg.getAttribute("width") || "0");
          let h = parseFloat(svg.getAttribute("height") || "0");

          if (!vb) {
            if (w && h) {
              vb = `0 0 ${w} ${h}`;
            } else {
              // Fallback if no dimensions found
              vb = "0 0 256 256";
              w = 256;
              h = 256;
            }
          } else {
            const parts = vb.split(/[\s,]+/).map(parseFloat);
            if (parts.length === 4) {
              w = parts[2];
              h = parts[3];
            }
          }

          setViewBox(vb);
          const ratio = w / h || 1;
          setAspectRatio(ratio);

          if (!svg.getAttribute("viewBox")) {
            svg.setAttribute("viewBox", vb);
          }
          svg.removeAttribute("width");
          svg.removeAttribute("height");
          if (!svg.getAttribute("xmlns")) {
            svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
          }

          setSvgContent(new XMLSerializer().serializeToString(svg));
          setRasterSrc(null);
          setImageEl(null);

          // Reset state
          setZoom(1);

          if (ratio > 1) {
            setPosition({ x: 0, y: (256 - 256 / ratio) / 2 });
          } else {
            setPosition({ x: (256 - 256 * ratio) / 2, y: 0 });
          }
        } catch (err) {
          setLoadError(
            `Couldn't read ${file.name} as an SVG: ${
              err instanceof Error ? err.message : String(err)
            }`,
          );
        }
      };
      reader.readAsText(file);

      return () => {
        active = false;
      };
    } else {
      // Raster image. The blob URL is created and handed to the image on the
      // next task, not here: StrictMode's mount → cleanup → mount happens
      // synchronously, so the first run's timer is cleared before it fires
      // and only the surviving run ever creates a URL and starts a load. Doing
      // it inline left the first image fetching a URL its cleanup had just
      // revoked — a red `net::ERR_FILE_NOT_FOUND` in the console per upload.
      let url: string | null = null;
      const timer = setTimeout(() => {
        const src = URL.createObjectURL(file);
        url = src;
        const img = new Image();
        img.onload = () => {
          if (!active) return;
          const w = img.width;
          const h = img.height;
          const ratio = w / h || 1;
          setAspectRatio(ratio);

          setRasterSrc(src);
          setImageEl(img);
          setSvgContent(null);
          setViewBox(null);

          setZoom(1);

          if (ratio > 1) {
            setPosition({ x: 0, y: (256 - 256 / ratio) / 2 });
          } else {
            setPosition({ x: (256 - 256 * ratio) / 2, y: 0 });
          }
        };
        img.onerror = () => {
          if (active)
            setLoadError(
              `Couldn't read ${file.name} as an image. Pick a PNG, WebP or SVG.`,
            );
        };
        img.src = src;
      }, 0);

      return () => {
        active = false;
        clearTimeout(timer);
        if (url) URL.revokeObjectURL(url);
      };
    }
  }, [file]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    lastMousePos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const dx = e.clientX - lastMousePos.current.x;
    const dy = e.clientY - lastMousePos.current.y;
    lastMousePos.current = { x: e.clientX, y: e.clientY };
    setPosition((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const getDistance = (t1: React.Touch, t2: React.Touch) => {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      lastMousePos.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    } else if (e.touches.length === 2) {
      setIsDragging(false);
      const dist = getDistance(e.touches[0], e.touches[1]);
      lastPinchDistance.current = dist;
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && isDragging) {
      const dx = e.touches[0].clientX - lastMousePos.current.x;
      const dy = e.touches[0].clientY - lastMousePos.current.y;
      lastMousePos.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
      setPosition((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
    } else if (e.touches.length === 2 && lastPinchDistance.current !== null) {
      const dist = getDistance(e.touches[0], e.touches[1]);
      const zoomFactor = dist / lastPinchDistance.current;
      setZoom((prevZoom) => Math.min(Math.max(prevZoom * zoomFactor, 0.1), 5));
      lastPinchDistance.current = dist;
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    lastPinchDistance.current = null;
  };

  const previewBaseSize = useMemo(() => {
    if (aspectRatio > 1) {
      return { w: 256, h: 256 / aspectRatio };
    }
    return { w: 256 * aspectRatio, h: 256 };
  }, [aspectRatio]);

  const handleSave = () => {
    const finalW = previewBaseSize.w * zoom;
    const finalH = previewBaseSize.h * zoom;
    const finalX = position.x;
    const finalY = position.y;

    if (svgContent && viewBox) {
      // It's an SVG.
      const finalSvgStr = `<svg xmlns="http://www.w3.org/2000/svg" width="256" height="256" viewBox="0 0 256 256">
      ${(() => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgContent, "image/svg+xml");
        const root = doc.documentElement;

        root.setAttribute("x", String(finalX));
        root.setAttribute("y", String(finalY));
        root.setAttribute("width", String(finalW));
        root.setAttribute("height", String(finalH));

        return new XMLSerializer().serializeToString(root);
      })()}
  </svg>`;

      const blob = new Blob([finalSvgStr], { type: "image/svg+xml" });
      onSave(blob);
    } else if (rasterSrc && imageEl) {
      // It's a raster image. Draw it to a canvas and save as webp.
      const canvas = document.createElement("canvas");
      canvas.width = 256;
      canvas.height = 256;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Make circular mask
      ctx.beginPath();
      ctx.arc(128, 128, 128, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();

      // Draw the image transformed
      ctx.drawImage(imageEl, finalX, finalY, finalW, finalH);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            onSave(blob);
          }
        },
        "image/webp",
        0.9,
      );
    }
  };

  const hasContent = !!(svgContent || rasterSrc);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Crop Logo</DialogTitle>
      {loadError && (
        <Alert severity="error" sx={{ mx: 3 }}>
          {loadError}
        </Alert>
      )}
      <DialogContent>
        <Stack
          spacing={2}
          sx={{
            alignItems: "center",
            mt: 1,
            userSelect: "none",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            Drag to pan, use slider to zoom. Result will be cropped to circle.
          </Typography>

          {/* Editor Container */}
          <Box
            sx={{
              width: 256,
              height: 256,
              position: "relative",
              overflow: "hidden",
              bgcolor: "#f0f0f0",
              cursor: isDragging ? "grabbing" : "grab",
              border: "1px solid #ccc",
              borderRadius: "50%",
              touchAction: "none",
            }}
            data-testid="logo-editor-canvas"
            ref={containerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Checkboard pattern for transparency */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundImage: "radial-gradient(#ccc 1px, transparent 1px)",
                backgroundSize: "10px 10px",
                opacity: 0.5,
                zIndex: 0,
              }}
            />

            {/* Content SVG */}
            {svgContent && (
              <Box
                component="div"
                dangerouslySetInnerHTML={{ __html: svgContent }}
                sx={{
                  position: "absolute",
                  left: position.x,
                  top: position.y,
                  width: previewBaseSize.w * zoom,
                  height: previewBaseSize.h * zoom,
                  transformOrigin: "top left",
                  pointerEvents: "none", // Let mouse events fall through to container
                  "& svg": {
                    width: "100%",
                    height: "100%",
                    overflow: "visible",
                  },
                }}
              />
            )}

            {/* Content Raster */}
            {rasterSrc && (
              <Box
                component="img"
                src={rasterSrc}
                alt="preview"
                sx={{
                  position: "absolute",
                  left: position.x,
                  top: position.y,
                  width: previewBaseSize.w * zoom,
                  height: previewBaseSize.h * zoom,
                  transformOrigin: "top left",
                  pointerEvents: "none",
                }}
              />
            )}

            {/* Circular Overlay / Guide */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                border: "2px solid rgba(0,0,0,0.3)",
                borderRadius: "50%",
                pointerEvents: "none",
                boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)", // Dim outside
              }}
            />
          </Box>

          {/* Controls */}
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: "center",
              width: "100%",
              px: 4,
            }}
          >
            <IconButton
              onClick={() => setZoom((z) => Math.max(0.1, z - 0.1))}
              aria-label="Zoom Out"
            >
              <ZoomOutIcon color="action" />
            </IconButton>
            <Slider
              value={zoom}
              min={0.1}
              max={5}
              step={0.1}
              onChange={(_, v) => setZoom(v as number)}
              aria-label="Zoom"
            />
            <IconButton
              onClick={() => setZoom((z) => Math.min(5, z + 0.1))}
              aria-label="Zoom In"
            >
              <ZoomInIcon color="action" />
            </IconButton>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!hasContent}>
          Set Logo
        </Button>
      </DialogActions>
    </Dialog>
  );
};
